import {
  IconDocumentLine,
  IconImageLine,
  IconMsExcelLine,
  IconMsPptLine,
  IconMsWordLine,
  IconPdfLine,
  IconVideoLine,
  IconZippedLine,
} from '@instructure/ui';
import moment from 'moment';
import { ToastTypeEnum } from '../enum/common';
import { toast } from 'react-toastify';
import { TIME_CLOSE_TOAST, TIME_OUT_SHOW_TOAST_ERROR, TIME_OUT_SHOW_TOAST_SUCCESS } from '../constants/common';

export const convertBase64ToFile = (base64String: string) => {
  const matches = base64String.match(/^data:(.*);base64,(.*)$/);
  if (!matches) return;

  const contentType = matches[1];
  const base64Data = matches[2];

  const byteString = atob(base64Data);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new File([ab], 'image.jpg', { type: contentType });
};

export const generateGuid = (): string => {
  const generateSegment = () => {
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  };

  const guid =
    generateSegment() +
    generateSegment() +
    '-' +
    generateSegment() +
    '-' +
    generateSegment() +
    '-' +
    generateSegment() +
    '-' +
    generateSegment() +
    generateSegment() +
    generateSegment();

  return guid;
};

export const getFileIconByMimetype = (mimetype: string) => {
  const archiveTypes = [
    'application/zip',
    'application/x-zip-compressed',
    'application/vnd.rar',
    'application/x-rar-compressed',
    'application/x-rar',
    'application/x-7z-compressed',
    'application/x-tar',
    'application/gzip',
    'application/x-gzip',
    'application/x-bzip2',
    'application/x-xz',
  ];

  const wordTypes = [
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
    'application/vnd.ms-word.document.macroEnabled.12',
    'application/vnd.ms-word.template.macroEnabled.12',
  ];

  const pptTypes = [
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.openxmlformats-officedocument.presentationml.template',
    'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
    'application/vnd.ms-powerpoint.addin.macroEnabled.12',
    'application/vnd.ms-powerpoint.presentation.macroEnabled.12',
    'application/vnd.ms-powerpoint.template.macroEnabled.12',
    'application/vnd.ms-powerpoint.slideshow.macroEnabled.12',
  ];

  const excelTypes = [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
    'application/vnd.ms-excel.sheet.macroEnabled.12',
    'application/vnd.ms-excel.template.macroEnabled.12',
    'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
  ];

  if (mimetype.startsWith('video/')) {
    return IconVideoLine;
  }

  if (mimetype.startsWith('image/')) {
    return IconImageLine;
  }

  if (mimetype.startsWith('application/pdf')) {
    return IconPdfLine;
  }

  if (excelTypes.includes(mimetype)) {
    return IconMsExcelLine;
  }

  if (pptTypes.includes(mimetype)) {
    return IconMsPptLine;
  }

  if (wordTypes.includes(mimetype)) {
    return IconMsWordLine;
  }

  if (archiveTypes.includes(mimetype)) {
    return IconZippedLine;
  }

  return IconDocumentLine;
};

export const formatDateTimeLabel = (isoString: string, atLabel: string, showFull: boolean = false) => {
  const date = moment(isoString);
  const now = moment();

  if (date.isSame(now, 'day') && !showFull) {
    return date.format('h:mma');
  }

  if (date.isSame(now, 'year') && !showFull) {
    return date.format(`MMM D [${atLabel}] h:mma`);
  }

  return date.format(`MMM D, YYYY [${atLabel}] h:mma`);
};

export const customToast = (type: ToastTypeEnum, message: string) => {
  switch (type) {
    case ToastTypeEnum.ERROR:
      setTimeout(() => {
        toast.error(message, {
          autoClose: TIME_CLOSE_TOAST,
        });
      }, TIME_OUT_SHOW_TOAST_ERROR);
      break;
    case ToastTypeEnum.SUCCESS:
      setTimeout(() => {
        toast.success(message, {
          autoClose: TIME_CLOSE_TOAST,
        });
      }, TIME_OUT_SHOW_TOAST_SUCCESS);
      break;
    case ToastTypeEnum.WARNING:
      toast.warning(message, {
        autoClose: TIME_CLOSE_TOAST,
      });
      break;
    default:
      toast(message, {
        autoClose: TIME_CLOSE_TOAST,
      });
      break;
  }
};

export const getTimeRemaining = (endAt: string) => {
  const end = new Date(endAt).getTime();
  const now = Date.now();
  let diff = Math.max(0, end - now);

  const hours = Math.floor(diff / (1000 * 60 * 60));
  diff %= 1000 * 60 * 60;

  const minutes = Math.floor(diff / (1000 * 60));
  diff %= 1000 * 60;

  const seconds = Math.floor(diff / 1000);

  return [String(hours).padStart(2, '0'), String(minutes).padStart(2, '0'), String(seconds).padStart(2, '0')].join(':');
};

export const getTimeElapsed = (startAt: string) => {
  const startTime = new Date(startAt).getTime();
  const now = Date.now();
  const diff = now - startTime;

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  return [String(hours).padStart(2, '0'), String(minutes).padStart(2, '0'), String(seconds).padStart(2, '0')].join(':');
};
